import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  CardHeadeWithActions,
  MwButton,
  MwInputText,
  MwSelector,
  MwSpinnerButton,
  NoDataFound,
  Pagination,
  ConfirmDelete,
} from "../components/ui";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import JournalEntryContext from "../utils/JournalEntryHelper";
import JournalEntryPrintScreen from "../printsScreen/JournalEntryPrintScreen";
import axios from "axios";
import { toast } from "react-toastify";
import { useTenant } from "../hooks";

export const JournalEntry = () => {
  const [t] = useTranslation("global");
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { tenantId, branchSelected, companySelected } = useTenant();
  const baseUrl = process.env.REACT_APP_INV_API_SERVER_BASE_URL;
  const {
    entries: JournalEntries,
    loading,
    pagination,
    fetchJournalEntries,
    dispatch,
    selectedPeriod,
    isPeriodClosed,
    deleteEntry,
  } = useContext(JournalEntryContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [isPostingSelected, setIsPostingSelected] = useState(false);
  const [filter, setFilter] = useState({
    from: "",
    to: "",
    state: "",
    number: "",
    description: "",
  });

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewEntry, setPreviewEntry] = useState(null);
  const [autoPrintPreview, setAutoPrintPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  useEffect(() => {
    if (
      !tenantId ||
      !companySelected?.value ||
      !branchSelected?.value ||
      !selectedPeriod
    ) {
      return;
    }
    fetchJournalEntries(currentPage);
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [tenantId, companySelected?.value, branchSelected?.value, selectedPeriod]);

  const getSelectedValue = (selected) =>
    typeof selected === "string" ? selected : selected?.value || "";

  const toDateKey = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const filteredJournalEntries = useMemo(() => {
    const selectedState = getSelectedValue(filter.state).toLowerCase();
    const numberQuery = String(filter.number || "").trim();
    const descriptionQuery = String(filter.description || "")
      .trim()
      .toLowerCase();

    return (JournalEntries || []).filter((entry) => {
      const entryDate = toDateKey(entry.trans_date_m);
      const entryStatus = entry.is_completed ? "posted" : "unposted";
      const entryNumber = String(entry?.trans_no ?? "");
      const entryDescription = String(entry?.trans_desc ?? "").toLowerCase();

      const fromMatch = !filter.from || (entryDate && entryDate >= filter.from);
      const toMatch = !filter.to || (entryDate && entryDate <= filter.to);
      const stateMatch =
        !selectedState ||
        selectedState === "all" ||
        entryStatus === selectedState;
      const numberMatch = !numberQuery || entryNumber.includes(numberQuery);
      const descriptionMatch =
        !descriptionQuery || entryDescription.includes(descriptionQuery);

      return (
        fromMatch && toMatch && stateMatch && numberMatch && descriptionMatch
      );
    });
  }, [
    JournalEntries,
    filter.from,
    filter.to,
    filter.state,
    filter.number,
    filter.description,
  ]);

  const selectableEntryIds = useMemo(
    () =>
      filteredJournalEntries
        .filter((entry) => !entry?.is_completed && entry?.id != null)
        .map((entry) => entry.id),
    [filteredJournalEntries],
  );

  const isAllSelected =
    selectableEntryIds.length > 0 &&
    selectableEntryIds.every((id) => selectedEntryIds.includes(id));

  const selectedEntries = useMemo(
    () =>
      filteredJournalEntries.filter((entry) =>
        selectedEntryIds.includes(entry?.id),
      ),
    [filteredJournalEntries, selectedEntryIds],
  );

  useEffect(() => {
    setSelectedEntryIds((prev) =>
      prev.filter((id) => selectableEntryIds.includes(id)),
    );
  }, [selectableEntryIds]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleEntrySelection = (entryId, checked) => {
    setSelectedEntryIds((prev) => {
      if (checked) {
        return prev.includes(entryId) ? prev : [...prev, entryId];
      }

      return prev.filter((id) => id !== entryId);
    });
  };

  const handleSelectAllEntries = (checked) => {
    setSelectedEntryIds(checked ? selectableEntryIds : []);
  };

  const handlePostSelectedEntries = async () => {
    if (isPeriodClosed || isPostingSelected) return;

    if (selectedEntries.length === 0) {
      toast.warning(t("JournalEntry.selectEntriesFirst"));
      return;
    }

    setIsPostingSelected(true);

    try {
      const ids = selectedEntries
        .map((entry) => entry?.id)
        .filter((id) => id !== null && id !== undefined);

      if (
        ids.length === 0 ||
        !tenantId ||
        !companySelected?.value ||
        !branchSelected?.value ||
        !selectedPeriod
      ) {
        toast.error(t("JournalEntry.postSelectedError"));
        return;
      }

      await axios.post(`${baseUrl}/transactions/completed`, {
        tenant_id: tenantId,
        company_id: companySelected?.value,
        branch_id: branchSelected?.value,
        fin_year_id: selectedPeriod,
        ids,
      });

      await fetchJournalEntries(currentPage);
      setSelectedEntryIds([]);
      toast.success(
        t("JournalEntry.postSelectedSuccess", { count: ids.length }),
      );
    } catch (error) {
      console.error("Post selected entries error:", error);
      toast.error(t("JournalEntry.postSelectedError"));
    } finally {
      setIsPostingSelected(false);
    }
  };

  const openPreview = (entry, shouldPrint = false) => {
    setPreviewEntry(entry);
    setAutoPrintPreview(shouldPrint);
    setIsPreviewOpen(true);
  };
  const getEntryData = (mode, entry = null) => {
    if (isPeriodClosed) return;
    const search = `?mode=${encodeURIComponent(mode || "ADD")}`;
    navigate(`/${tenant}/Journalmangement${search}`, {
      state: {
        mode,
        periodId: selectedPeriod || null,
        entry,
      },
    });
  };
  const openJournalManagementPage = (mode, entry = null) => {
    if (mode !== "VIEW" && isPeriodClosed) return;
    if (mode === "ADD") {
      getEntryData(mode, entry);
      return;
    }

    const transNo = encodeURIComponent(entry?.trans_no ?? "");
    const entryId = encodeURIComponent(entry?.id ?? "");
    const search = `?mode=${encodeURIComponent(
      mode || "VIEW",
    )}&trans_no=${transNo}&id=${entryId}`;

    navigate(`/${tenant}/Journalmangement${search}`, {
      state: {
        mode,
        periodId: selectedPeriod || null,
        entry,
      },
    });
  };

  const handleDeleteClick = (entry) => {
    if (entry?.is_completed) return;
    setEntryToDelete(entry);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!entryToDelete) return;
    if (isPeriodClosed) return;
    try {
      await deleteEntry(entryToDelete.id);
      dispatch({
        type: "DELETE_ENTRY",
        payload: { id: entryToDelete.id },
      });
      setShowDeleteConfirm(false);
      setEntryToDelete(null);
    } catch (error) {
      console.log(error);
      setShowDeleteConfirm(false);
      setEntryToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setEntryToDelete(null);
  };
  return (
    <section className="p-2 font-normal">
      <CardHeadeWithActions
        title={t("menu.JournalEntry")}
        icon={"/assets/icons/notebookcolor.png"}
      >
        <MwButton
          classNameCustom={`w-max ${isPeriodClosed ? "cursor-not-allowed" : "cursor-pointer"} !font-normal text-white bg-[#261759] hover:bg-[#463977]`}
          children={t("common.AddJornalEntry")}
          type="newBtn"
          disabled={isPeriodClosed}
          onClick={() => openJournalManagementPage("ADD")}
        />
      </CardHeadeWithActions>

      {(JournalEntries || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-4 mt-4">
          <div className="w-[10%]">
            <MwInputText
              label={t("JournalEntry.numofentry")}
              inputType="text"
              value={filter.number}
              placeholder={t("JournalEntry.numofentry")}
              onChange={(e) => setFilter({ ...filter, number: e.target.value })}
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <MwInputText
              label={t("JournalEntry.description")}
              inputType="text"
              value={filter.description}
              placeholder={t("JournalEntry.description")}
              onChange={(e) =>
                setFilter({ ...filter, description: e.target.value })
              }
            />
          </div>
          <div className="w-[15%]">
            <MwInputText
              label={t("entriesFilter.from")}
              inputType="date"
              value={filter.from}
              onChange={(e) => setFilter({ ...filter, from: e.target.value })}
            />
          </div>
          <div className="w-[15%]">
            <MwInputText
              label={t("entriesFilter.to")}
              inputType="date"
              value={filter.to}
              onChange={(e) => setFilter({ ...filter, to: e.target.value })}
            />
          </div>
          <div className="w-[15%]">
            <MwSelector
              _data={[
                {
                  id: "0",
                  label: t("common.all"),
                  value: "all",
                  lang: i18next.language,
                },
                {
                  id: "1",
                  label: t("entriesFilter.posted"),
                  value: "posted",
                  lang: i18next.language,
                },
                {
                  id: "2",
                  label: t("entriesFilter.unposted"),
                  value: "unposted",
                  lang: i18next.language,
                },
              ]}
              withAddNew={false}
              withSearch={false}
              label={t("entriesFilter.state")}
              dataType="labelValue"
              classnameSelect="font-normal"
              selectedItem={filter?.state}
              setSelectedItem={(e) => setFilter({ ...filter, state: e })}
            />
          </div>
        </div>
      )}

      <div className="flex justify-center items-start w-full h-full mt-3">
        {loading ? (
          <div className="w-full h-[30vh] flex justify-center items-center">
            <MwSpinnerButton />
          </div>
        ) : filteredJournalEntries.length === 0 ? (
          <div className="w-[50%] mt-32 flex justify-center items-center">
            <NoDataFound msg={t("common.noData")} />
          </div>
        ) : (
          <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full">
              <thead className="bg-[#ebf9d9]">
                <tr className="text-center text-sm text-[#261759]">
                  <th className="p-2 font-normal w-[60px] whitespace-nowrap align-middle">
                    {t("JournalEntry.numofentry")}
                  </th>
                  <th className="p-2 font-normal w-[110px] whitespace-nowrap align-middle">
                    {t("JournalEntry.date")}
                  </th>
                  <th className="p-2 text-start font-normal flex-1 min-w-[200px] align-middle">
                    {t("JournalEntry.description")}
                  </th>
                  <th className="p-2 font-normal w-[120px] whitespace-nowrap align-middle">
                    {t("JournalEntry.previosnum")}
                  </th>
                  <th className="p-2 font-normal w-[110px] whitespace-nowrap align-middle">
                    {t("entriesFilter.state")}
                  </th>
                  <th className="p-2 font-normal w-[110px] whitespace-nowrap ">
                    {t("times.actions")}
                  </th>
                  <th className="p-2 font-normal w-[320px] whitespace-nowrap align-middle">
                    <div className="mx-auto flex w-fit flex-wrap items-center justify-center gap-2 rounded-2xl border border-[#d5e7bc] bg-white px-3 py-2 shadow-sm">
                      <span className="font-medium">
                        {t("JournalEntry.post")}
                      </span>
                      <span className="rounded-full bg-[#ebf9d9] px-2 py-0.5 text-xs font-medium text-[#261759]">
                        {selectedEntryIds.length}/{selectableEntryIds.length}
                      </span>
                      <label
                        className={`flex items-center gap-1.5 rounded-full px-2 py-1 text-xs ${
                          selectableEntryIds.length === 0
                            ? "cursor-not-allowed opacity-60"
                            : "cursor-pointer hover:bg-[#f7faef]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#261759]"
                          checked={isAllSelected}
                          onChange={(e) =>
                            handleSelectAllEntries(e.target.checked)
                          }
                          disabled={selectableEntryIds.length === 0}
                        />
                        <span className="font-medium">{t("common.all")}</span>
                      </label>
                      <button
                        type="button"
                        onClick={handlePostSelectedEntries}
                        disabled={
                          isPeriodClosed ||
                          isPostingSelected ||
                          selectedEntryIds.length === 0
                        }
                        className={`rounded-full px-3 py-1.5 text-xs font-medium text-white transition-all ${
                          isPeriodClosed ||
                          isPostingSelected ||
                          selectedEntryIds.length === 0
                            ? "cursor-not-allowed bg-slate-400"
                            : "bg-[#261759] hover:bg-[#463977]"
                        }`}
                      >
                        {isPostingSelected
                          ? t("JournalEntry.postingSelected")
                          : t("JournalEntry.postSelected")}
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredJournalEntries.map((d) => (
                  <tr
                    key={d.id}
                    className={`border-b text-sm transition-all ${
                      selectedEntryIds.includes(d.id)
                        ? "bg-[#f7f3ff] hover:bg-[#f1e9ff]"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <td className="p-2 text-center text-[#261759] w-[60px] whitespace-nowrap align-middle">
                      {d.trans_no || "-"}
                    </td>

                    <td className="p-2 text-center text-[#261759] w-[110px] whitespace-nowrap align-middle">
                      {d.trans_date_m || "-"}
                    </td>

                    <td className="p-2 text-start text-[#261759] flex-1 min-w-[200px] break-words align-middle">
                      {d.trans_desc || "-"}
                    </td>

                    <td className="p-2 text-center text-[#261759] w-[120px] whitespace-nowrap align-middle">
                      {d.trans_ref_no || "-"}
                    </td>

                    <td className="p-2 text-center text-[#261759] w-[110px] whitespace-nowrap align-middle">
                      {d.is_completed
                        ? t("entriesFilter.posted")
                        : t("entriesFilter.unposted")}
                    </td>

                    <td className="p-2 w-[110px] whitespace-nowrap align-middle h-full">
                      <div className="flex justify-center items-center gap-2 h-full">
                        <button
                          onClick={() => openJournalManagementPage("VIEW", d)}
                        >
                          <img
                            src="/assets/icons/show.png"
                            alt="view"
                            className="w-[14px]"
                          />
                        </button>

                        <button onClick={() => openPreview(d, false)}>
                          <img
                            src="/assets/icons/Print.png"
                            alt="print"
                            className="w-[13px]"
                          />
                        </button>

                        {!isPeriodClosed && (
                          <>
                            <button
                              onClick={() =>
                                openJournalManagementPage("EDIT", d)
                              }
                            >
                              <img
                                src="/assets/icons/Edit.png"
                                alt=""
                                className="w-[13px]"
                              />
                            </button>

                            <button
                              onClick={() => handleDeleteClick(d)}
                              disabled={d.is_completed}
                              className={
                                d.is_completed
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }
                            >
                              <img
                                src="/assets/icons/Trash.png"
                                alt=""
                                className="w-[13px]"
                              />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="p-2 w-[180px] align-middle">
                      <div className="flex justify-center items-center h-full">
                        <label
                          className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                            d.is_completed
                              ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                              : selectedEntryIds.includes(d.id)
                                ? "border-[#cdbbff] bg-[#efe7ff] text-[#261759] shadow-sm"
                                : "cursor-pointer border-slate-200 bg-white text-[#261759] hover:border-[#cdbbff] hover:bg-[#faf7ff]"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[#261759]"
                            checked={selectedEntryIds.includes(d.id)}
                            disabled={d.is_completed}
                            onChange={(e) =>
                              handleEntrySelection(d.id, e.target.checked)
                            }
                          />
                          <span>
                            {d.is_completed
                              ? t("entriesFilter.posted")
                              : t("JournalEntry.post")}
                          </span>
                        </label>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <JournalEntryPrintScreen
        onShow={isPreviewOpen}
        setOnShow={setIsPreviewOpen}
        entry={previewEntry}
        autoPrint={autoPrintPreview}
        onAutoPrintHandled={() => setAutoPrintPreview(false)}
      />

      <Pagination
        currentPage={pagination?.current_page || 1}
        totalPages={pagination?.last_page || 1}
        onPageChange={handlePageChange}
      />

      {showDeleteConfirm && (
        <ConfirmDelete
          title={t("common.deletemsg")}
          msg={`${t("JournalEntry.coDelete")}: ${entryToDelete?.trans_no}`}
          onDeleteHandler={confirmDelete}
          onCancelHandle={cancelDelete}
          loading={loading}
          onShow={showDeleteConfirm}
          setOnShow={setShowDeleteConfirm}
          type={"delete"}
        />
      )}
    </section>
  );
};
